import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Group,
  Text,
  Title,
  LoadingOverlay,
  Badge,
  ActionIcon,
  Tooltip,
  Alert,
  Space,
  Grid,
  Stack,
  Select,
  TextInput,
  Modal,
  Switch,
  MultiSelect,
  Tabs,
} from '@mantine/core';
import {
  IconDownload,
  IconUpload,
  IconDeviceFloppy,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconTemplate,
  IconDatabase,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useSearchParams } from 'react-router-dom';
import { get, set } from 'idb-keyval';

import { MyUserDataContext } from '../../contexts';
import {
  BaseSchema,
  TemplateDoc,
  SchemaConfiguratorState,
  SaveStatus,
  BaseSchemaSchema,
  TemplateDocSchema,
} from '../../types/geneva-schema';
import { loadBaseSchema, getSchemaDefaults } from '../../utils/geneva-schema-loader';
import { useGenevaSchemaService } from './hooks/useGenevaSchemaService';
import { GenevaRecordsList } from './components/GenevaRecordsList';
import { GenevaFieldsTable } from './components/GenevaFieldsTable';
import { getInheritanceChain } from '../../utils/geneva-schema-helpers';
import { GenevaFieldDrawer } from './components/GenevaFieldDrawer';
import { ValidationTemplatesManager } from './components/ValidationTemplatesManager';
import { DEFAULT_SCHEMA_TYPE } from '../../utils/schema-config';
import { SchemaType, schemaTypeOptions } from '../../types/schema';

const AUTOSAVE_DELAY = 750;

const createInitialState = (): SchemaConfiguratorState => ({
  schema: null,
  template: null,
  selectedRecord: null,
  loading: true,
  saving: false,
  error: null,
  searchQuery: '',
  selectedField: null,
  drawerOpen: false,
  dirty: false,
});

export const GenevaSchemaConfigurator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userContext = useContext(MyUserDataContext);

  // URL parameters
  const orgId = searchParams.get('orgId') || 'demo';
  const rawSchemaType = searchParams.get('schemaType');
  const initialSchemaType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
  const templateParam = searchParams.get('templateId');
  const defaultTemplateId = initialSchemaType === 'gwi' ? 'gwi' : 'default';
  const templateId = templateParam || defaultTemplateId;

  const [schemaType, setSchemaType] = useState<SchemaType>(initialSchemaType);
  const schemaDefaults = useMemo(() => getSchemaDefaults(schemaType), [schemaType]);
  const schemaLabel = schemaDefaults.displayName;

  // State
  const [state, setState] = useState<SchemaConfiguratorState>(() => createInitialState());

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [createVirtualModal, setCreateVirtualModal] = useState(false);
  const [newVirtualName, setNewVirtualName] = useState('');
  const [newVirtualBase, setNewVirtualBase] = useState<string | null>(null);
  const [isMultiRecord, setIsMultiRecord] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'records' | 'templates'>('records');

  useEffect(() => {
    const nextType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
    if (nextType !== schemaType) {
      setSchemaType(nextType);
    }
  }, [rawSchemaType, schemaType]);

  const handleSchemaTypeChange = useCallback((value: string | null) => {
    if (!value) return;
    const nextType: SchemaType = value === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
    if (nextType === schemaType) return;
    setSchemaType(nextType);

    const params = new URLSearchParams(searchParams);
    const nextTemplateId = nextType === 'gwi' ? 'gwi' : 'default';
    params.set('schemaType', nextType);
    params.set('orgId', orgId);
    params.set('templateId', nextTemplateId);
    setSearchParams(params);
  }, [schemaType, searchParams, setSearchParams, orgId]);

  // Custom hook for Geneva schema operations
  const genevaService = useGenevaSchemaService({
    orgId,
    templateId,
    userId: userContext.user?.uid || 'anonymous',
    userEmail: userContext.user?.email || 'anonymous',
    schemaType,
  });

  // Load schema from cache or URL
  const loadSchema = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const schema = await loadBaseSchema({
        schemaType,
        url: schemaDefaults.schemaUrl,
        cacheKey: schemaDefaults.cacheKey,
      });
      setState(prev => ({ ...prev, schema }));
    } catch (error) {
      console.error('Failed to load schema:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load schema'
      }));
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [schemaDefaults, schemaType]);

  // Load template from Firestore
  const loadTemplate = useCallback(async () => {
    try {
      const template = await genevaService.loadTemplate();
      setState(prev => ({ ...prev, template }));
    } catch (error) {
      console.error('Failed to load template:', error);
      // Create default template if it doesn't exist
      const defaultTemplate: TemplateDoc = {
        schemaVersion: 'v1',
        schemaType,
        enabledRecords: {},
        fieldOverrides: {},
        updatedAt: Date.now(),
        updatedBy: userContext.user?.email || 'anonymous',
        name: `Template ${templateId}`,
        description: `${schemaLabel} schema template`,
        recordQuantityChecks: {},
      };

      setState(prev => ({ ...prev, template: defaultTemplate, dirty: true }));
    }
  }, [genevaService, templateId, userContext.user?.email, schemaType, schemaLabel]);

  // Auto-save functionality
  const saveTemplate = useCallback(async () => {
    const { template } = state;
    if (!template) return;

    try {
      setSaveStatus('saving');
      await genevaService.saveTemplate(template);
      setSaveStatus('saved');
      setLastSaved(new Date());
      setState(prev => ({ ...prev, dirty: false }));

      notifications.show({
        title: 'Template Saved',
        message: 'Your changes have been saved successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error) {
      console.error('Failed to save template:', error);
      setSaveStatus('error');
      notifications.show({
        title: 'Save Failed',
        message: error instanceof Error ? error.message : 'Failed to save template',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  }, [state.template, genevaService]);

  // Debounced auto-save
  useEffect(() => {
    if (!state.dirty || !state.template) return;

    setSaveStatus('dirty');
    const timeoutId = setTimeout(() => {
      saveTemplate();
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [state.dirty, state.template, saveTemplate]);

  // Initialize & reload when context changes
  useEffect(() => {
    setState(prev => ({
      ...createInitialState(),
      searchQuery: prev.searchQuery,
    }));
    setSaveStatus('saved');
    setLastSaved(null);
    loadSchema();
  }, [schemaType, templateId, loadSchema]);

  // Listen for record validation save events from the records list modal
  useEffect(() => {
    const handler = (e: any) => {
      const detail = e?.detail;
      if (!detail?.recordName) return;
      setState(prev => {
        if (!prev.template) return prev;
        const current = prev.template.recordValidations || {};
        // Handle both single rule object and array of rules
        const normalizedRules = detail.rules
          ? (Array.isArray(detail.rules) ? detail.rules : [detail.rules])
          : [];

        const rules = normalizedRules.map((r: any) => ({
          expr: r.expr,
          when: r.when || undefined,
          level: r.level || undefined,
          message: r.message || undefined,
        })).filter((r: any) => r.expr && r.expr.trim());
        const nextRecordValidations = { ...current } as any;
        if (rules.length > 0) {
          nextRecordValidations[detail.recordName] = rules;
        } else {
          delete nextRecordValidations[detail.recordName];
        }
        const newTemplate: TemplateDoc = {
          ...prev.template!,
          recordValidations: Object.keys(nextRecordValidations).length > 0 ? nextRecordValidations : undefined,
          updatedAt: Date.now(),
        } as TemplateDoc;
        return { ...prev, template: newTemplate, dirty: true };
      });
      notifications.show({ title: 'Saved', message: 'Record validations updated', color: 'green' });
    };
    window.addEventListener('save-record-validations', handler);
    return () => window.removeEventListener('save-record-validations', handler);
  }, []);

  useEffect(() => {
    if (state.schema && !state.template) {
      const initTemplate = async () => {
        try {
          const template = await genevaService.loadTemplate();
          setState(prev => ({ ...prev, template }));
        } catch (error) {
          console.error('Failed to load template:', error);
          const defaultTemplate: TemplateDoc = {
            schemaVersion: 'v1',
            schemaType,
            enabledRecords: {},
            fieldOverrides: {},
            updatedAt: Date.now(),
            updatedBy: userContext.user?.email || 'anonymous',
            name: `Template ${templateId}`,
            description: `${schemaLabel} schema template`,
            recordQuantityChecks: {},
          };

          setState(prev => ({ ...prev, template: defaultTemplate, dirty: true }));
        }
      };

      initTemplate();
    }
  }, [state.schema, state.template, genevaService, templateId, userContext.user?.email, schemaType, schemaLabel]);

  // Update handlers
  const updateRecordEnabled = useCallback((recordName: string, enabled: boolean) => {
    setState(prev => {
      if (!prev.template) return prev;

      // Check if this is a virtual record
      const isVirtual = prev.template.virtualRecords?.[recordName];

      let newTemplate;
      if (isVirtual) {
        // Update virtual record
        newTemplate = {
          ...prev.template,
          virtualRecords: {
            ...prev.template.virtualRecords,
            [recordName]: {
              ...prev.template.virtualRecords![recordName],
              enabled,
            },
          },
          updatedAt: Date.now(),
        };
      } else {
        // Update real record
        newTemplate = {
          ...prev.template,
          enabledRecords: {
            ...prev.template.enabledRecords,
            [recordName]: enabled,
          },
          updatedAt: Date.now(),
        };
      }

      return {
        ...prev,
        template: newTemplate,
        dirty: true,
      };
    });
  }, []);

  const updateRecordQuantityCheck = useCallback((recordName: string, enabled: boolean) => {
    setState(prev => {
      if (!prev.template) return prev;

      const nextChecks = (() => {
        if (enabled) {
          return {
            ...(prev.template.recordQuantityChecks || {}),
            [recordName]: true,
          };
        }

        if (!prev.template.recordQuantityChecks) {
          return undefined;
        }

        const remaining = { ...prev.template.recordQuantityChecks };
        delete remaining[recordName];
        return Object.keys(remaining).length > 0 ? remaining : undefined;
      })();

      const newTemplate: TemplateDoc = {
        ...prev.template,
        recordQuantityChecks: nextChecks,
        updatedAt: Date.now(),
      };

      return {
        ...prev,
        template: newTemplate,
        dirty: true,
      };
    });
  }, []);

  const renderQuantityWarningToggle = useCallback((recordId: string | null) => {
    if (!recordId || !state.template) return null;

    const isEnabled = Boolean(state.template.recordQuantityChecks?.[recordId]);

    return (
      <Stack gap={4} mb="md">
        <Switch
          size="sm"
          label="Enable quantity availability warning"
          checked={isEnabled}
          onChange={(event) => updateRecordQuantityCheck(recordId, event.currentTarget.checked)}
        />
        <Text size="xs" c="dimmed">
          When enabled, the form warns if the requested quantity exceeds the available portfolio position.
        </Text>
      </Stack>
    );
  }, [state.template, updateRecordQuantityCheck]);

  const updateMultiRecordFieldOverride = useCallback((virtualRecordId: string, baseRecordName: string, fieldXmlTag: string, override: any) => {
    setState(prev => {
      if (!prev.template) return prev;

      const virtualRecord = prev.template.virtualRecords?.[virtualRecordId];
      if (!virtualRecord || !virtualRecord.multiRecordConfig) return prev;

      // Find the config for this base record
      const configIndex = virtualRecord.multiRecordConfig.findIndex(c => c.recordName === baseRecordName);
      if (configIndex === -1) return prev;

      // Create updated multi-record config
      const updatedConfig = [...virtualRecord.multiRecordConfig];
      updatedConfig[configIndex] = {
        ...updatedConfig[configIndex],
        fieldOverrides: {
          ...updatedConfig[configIndex].fieldOverrides,
          [fieldXmlTag]: override,
        },
      };

      const newTemplate = {
        ...prev.template,
        virtualRecords: {
          ...prev.template.virtualRecords,
          [virtualRecordId]: {
            ...virtualRecord,
            multiRecordConfig: updatedConfig,
          },
        },
        updatedAt: Date.now(),
      };

      return {
        ...prev,
        template: newTemplate,
        dirty: true,
      };
    });
  }, []);

  const updateValidationTemplates = useCallback((templates: Record<string, any>) => {
    setState(prev => {
      if (!prev.template) return prev;

      const newTemplate = {
        ...prev.template,
        validationTemplates: Object.keys(templates).length > 0 ? templates : undefined,
        updatedAt: Date.now(),
      };

      return {
        ...prev,
        template: newTemplate,
        dirty: true,
      };
    });
  }, []);

  const updateFieldOverride = useCallback((recordName: string, fieldXmlTag: string, override: any) => {
    console.log(`[updateFieldOverride] Record: "${recordName}", Field: "${fieldXmlTag}"`);
    console.log(`[updateFieldOverride] Override:`, override);
    console.log(`[updateFieldOverride] Override keys:`, Object.keys(override));
    console.log(`[updateFieldOverride] Is empty override?`, Object.keys(override).length === 0);

    setState(prev => {
      if (!prev.template) return prev;

      // Check if this is a virtual record
      const isVirtual = prev.template.virtualRecords?.[recordName];

      // Allow composite keys for array fields (e.g., "NonCapExpenses.Amount")
      // This prevents conflicts when the same field name exists in both array and regular contexts

      let newTemplate;
      if (isVirtual) {
        // Update virtual record's field overrides
        const currentVirtualOverrides = prev.template.virtualRecords![recordName].fieldOverrides || {};

        // If override is empty, remove the field entirely
        const updatedOverrides = { ...currentVirtualOverrides };
        if (Object.keys(override).length === 0) {
          delete updatedOverrides[fieldXmlTag];
          console.log(`[updateFieldOverride] Deleted field "${fieldXmlTag}" from virtual record overrides`);
        } else {
          updatedOverrides[fieldXmlTag] = override;
          console.log(`[updateFieldOverride] Updated field "${fieldXmlTag}" in virtual record overrides`);
        }

        newTemplate = {
          ...prev.template,
          virtualRecords: {
            ...prev.template.virtualRecords,
            [recordName]: {
              ...prev.template.virtualRecords![recordName],
              fieldOverrides: updatedOverrides,
            },
          },
          updatedAt: Date.now(),
        };
      } else {
        // Update real record's field overrides
        const recordOverrides = prev.template.fieldOverrides[recordName] || {};

        // If override is empty, remove the field entirely
        const updatedOverrides = { ...recordOverrides };
        if (Object.keys(override).length === 0) {
          delete updatedOverrides[fieldXmlTag];
          console.log(`[updateFieldOverride] Deleted field "${fieldXmlTag}" from record overrides`);
        } else {
          updatedOverrides[fieldXmlTag] = override;
          console.log(`[updateFieldOverride] Updated field "${fieldXmlTag}" in record overrides`);
        }

        newTemplate = {
          ...prev.template,
          fieldOverrides: {
            ...prev.template.fieldOverrides,
            [recordName]: updatedOverrides,
          },
          updatedAt: Date.now(),
        };
      }

      console.log(`[updateFieldOverride] New template fieldOverrides for "${recordName}":`, newTemplate.fieldOverrides[recordName]);

      return {
        ...prev,
        template: newTemplate,
        dirty: true,
      };
    });
  }, []);

  // Export functionality
  const handleExport = useCallback(() => {
    if (!state.template) return;

    const exportData = {
      template: state.template,
      metadata: {
        exportedAt: Date.now(),
        exportedBy: userContext.user?.email || 'anonymous',
        schemaVersion: state.template.schemaVersion,
        schemaType: state.template.schemaType,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${schemaType}-template-${templateId}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    notifications.show({
      title: 'Template Exported',
      message: `${schemaLabel} template has been downloaded successfully`,
      color: 'green',
    });
  }, [state.template, templateId, userContext.user?.email, schemaLabel, schemaType]);

  // Import functionality (basic - would need file input)
  const handleImport = useCallback(() => {
    // For now, just show notification - would need file picker in real implementation
    notifications.show({
      title: 'Import Feature',
      message: `Import functionality for ${schemaLabel} templates would be implemented here`,
      color: 'blue',
    });
  }, [schemaLabel]);

  // Create virtual record
  const handleCreateVirtualRecord = useCallback(async () => {
    if (!newVirtualName.trim()) return;

    // Validate inputs based on record type
    if (isMultiRecord && selectedRecords.length < 2) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select at least 2 records for a multi-record form',
        color: 'red',
      });
      return;
    }

    if (!isMultiRecord && !newVirtualBase) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a base record',
        color: 'red',
      });
      return;
    }

    try {
      const currentTemplate = state.template;
      if (!currentTemplate) return;

      // For single virtual records, validate for circular inheritance
      if (!isMultiRecord && newVirtualBase) {
        // Check if the proposed base record would create circular inheritance
        // We need to check if newVirtualBase eventually inherits from newVirtualName
        try {
          // First, create the proposed virtual ID to check against
          const proposedVirtualId = `${newVirtualBase}_${newVirtualName.replace(/\s+/g, '')}`;

          // Try to get the inheritance chain of the proposed base
          const baseChain = getInheritanceChain(newVirtualBase, currentTemplate);

          // Check if the proposed virtual record ID would be in the base's chain
          if (baseChain.includes(proposedVirtualId)) {
            notifications.show({
              title: 'Circular Inheritance Detected',
              message: `Cannot inherit from "${newVirtualBase}" as it would create a circular reference`,
              color: 'red',
            });
            return;
          }
        } catch (error) {
          // If we get an error checking inheritance chain, it might already be circular
          notifications.show({
            title: 'Inheritance Error',
            message: `Cannot validate inheritance chain for base record "${newVirtualBase}"`,
            color: 'red',
          });
          return;
        }
      }

      let virtualId: string;
      let virtualRecord: any;

      if (isMultiRecord) {
        // Generate ID for multi-record
        virtualId = `MultiRecord_${newVirtualName.replace(/\s+/g, '')}`;

        // Create multi-record configuration
        const multiRecordConfig = selectedRecords.map((recordName, index) => ({
          recordName,
          fieldOverrides: {},
          order: index,
        }));

        virtualRecord = {
          baseRecords: selectedRecords,
          multiRecordConfig,
          label: newVirtualName,
          enabled: true,
          fieldOverrides: {}, // Global overrides (backward compat)
          createdAt: Date.now(),
          createdBy: userContext.user?.email || 'unknown',
          isMultiRecord: true,
        };
      } else {
        // Single record (backward compatible)
        virtualId = `${newVirtualBase}_${newVirtualName.replace(/\s+/g, '')}`;

        virtualRecord = {
          base: newVirtualBase,
          label: `${newVirtualBase} – ${newVirtualName}`,
          enabled: true,
          fieldOverrides: {},
          createdAt: Date.now(),
          createdBy: userContext.user?.email || 'unknown',
        };
      }

      const updatedTemplate = {
        ...currentTemplate,
        virtualRecords: {
          ...currentTemplate.virtualRecords,
          [virtualId]: virtualRecord,
        },
        updatedAt: Date.now(),
      };

      setState(prev => ({
        ...prev,
        template: updatedTemplate,
        dirty: true
      }));

      notifications.show({
        title: 'Virtual Record Created',
        message: isMultiRecord
          ? `Multi-record form "${newVirtualName}" created with ${selectedRecords.length} records`
          : `Virtual record "${newVirtualName}" created based on ${newVirtualBase}`,
        color: 'green',
      });

    } catch (error) {
      console.error('Failed to create virtual record:', error);
      notifications.show({
        title: 'Create Failed',
        message: 'Failed to create virtual record',
        color: 'red',
      });
    }

    // Reset form
    setCreateVirtualModal(false);
    setNewVirtualName('');
    setNewVirtualBase(null);
    setIsMultiRecord(false);
    setSelectedRecords([]);
  }, [newVirtualName, newVirtualBase, isMultiRecord, selectedRecords, state.template, userContext.user?.email]);

  if (state.loading) {
    return (
      <Box pos="relative" h={400}>
        <LoadingOverlay visible />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Container>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Failed to Load Schema"
          color="red"
        >
          {state.error}
          <Group mt="md">
            <Button onClick={loadSchema} leftSection={<IconRefresh size={16} />}>
              Retry
            </Button>
          </Group>
        </Alert>
      </Container>
    );
  }

  const getSaveStatusDisplay = () => {
    switch (saveStatus) {
      case 'saved':
        return (
          <Group gap="xs">
            <IconCheck size={16} color="green" />
            <Text size="sm" c="green">
              Saved {lastSaved ? `• ${lastSaved.toLocaleTimeString()}` : ''}
            </Text>
          </Group>
        );
      case 'saving':
        return <Text size="sm" c="blue">Saving...</Text>;
      case 'error':
        return <Text size="sm" c="red">Error saving</Text>;
      case 'dirty':
        return <Text size="sm" c="orange">Unsaved changes</Text>;
      default:
        return null;
    }
  };

  return (
    <Container fluid>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={2}>{schemaLabel} Schema Configurator</Title>
          <Group gap="xs" mt="xs">
            <Text size="sm" c="dimmed">Org: {orgId}</Text>
            <Text size="sm" c="dimmed">•</Text>
            <Text size="sm" c="dimmed">Template: {state.template?.name || templateId}</Text>
            <Text size="sm" c="dimmed">•</Text>
            <Text size="sm" c="dimmed">
              Version: {state.template?.schemaVersion || 'v1'}
            </Text>
          </Group>
          <Group gap="xs" mt="xs">
            <Text size="sm" c="dimmed">
              Create virtual records for specialized variants (e.g., Portfolio-DPM, Portfolio-NonDPM)
            </Text>
          </Group>
        </Box>

        <Group>
          <Select
            size="sm"
            data={schemaTypeOptions}
            value={schemaType}
            onChange={handleSchemaTypeChange}
            aria-label="Schema type"
          />
          {getSaveStatusDisplay()}
          <Button
            size="sm"
            variant="light"
            onClick={() => setCreateVirtualModal(true)}
          >
            Create Virtual Record
          </Button>
          <ActionIcon
            variant="light"
            onClick={saveTemplate}
            loading={saveStatus === 'saving'}
            disabled={!state.dirty}
          >
            <IconDeviceFloppy size={16} />
          </ActionIcon>
          <ActionIcon variant="light" onClick={handleExport}>
            <IconDownload size={16} />
          </ActionIcon>
          <ActionIcon variant="light" onClick={handleImport}>
            <IconUpload size={16} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Stats */}
      {state.schema && (
        <Group mb="md">
          <Badge variant="light">
            {state.schema.records.length} Records
          </Badge>
          <Badge variant="light">
            {state.schema.records.reduce((acc, r) => acc + r.fields.length, 0)} Total Fields
          </Badge>
          {state.template && (
            <Badge variant="light" color="green">
              {Object.values(state.template.enabledRecords).filter(Boolean).length +
                Object.values(state.template.virtualRecords || {}).filter(vr => vr.enabled).length} Enabled Records
            </Badge>
          )}
          {state.template?.virtualRecords && Object.keys(state.template.virtualRecords).length > 0 && (
            <Badge variant="light" color="blue">
              {Object.keys(state.template.virtualRecords).length} Virtual Records
            </Badge>
          )}
        </Group>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value as 'records' | 'templates')}>
        <Tabs.List>
          <Tabs.Tab value="records" leftSection={<IconDatabase size="1rem" />}>
            Records & Fields
          </Tabs.Tab>
          <Tabs.Tab value="templates" leftSection={<IconTemplate size="1rem" />}>
            Validation Templates
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="records" pt="md">
          <Grid gap="md">
            <Grid.Col span={2}>
              <GenevaRecordsList
                schema={state.schema}
                template={state.template}
                selectedRecord={state.selectedRecord}
                onRecordSelect={(record: string) => setState(prev => ({ ...prev, selectedRecord: record }))}
                onRecordToggle={updateRecordEnabled}
                onRecordLookupFieldsChange={(recordName: string, fields: Array<{ name: string; alias?: string }>) => {
                  setState(prev => {
                    if (!prev.template) return prev;

                    const recordLookupFields = {
                      ...prev.template.recordLookupFields,
                    };

                    if (fields.length > 0) {
                      recordLookupFields[recordName] = fields;
                    } else {
                      delete recordLookupFields[recordName];
                    }

                    const newTemplate = {
                      ...prev.template,
                      recordLookupFields: Object.keys(recordLookupFields).length > 0 ? recordLookupFields : undefined,
                      updatedAt: Date.now(),
                    };

                    return {
                      ...prev,
                      template: newTemplate,
                      dirty: true,
                    };
                  });
                }}
                searchQuery={state.searchQuery}
                onSearchChange={(query: string) => setState(prev => ({ ...prev, searchQuery: query }))}
                validationTemplates={state.template?.validationTemplates}
              />
            </Grid.Col>

            <Grid.Col span={10}>
              {state.selectedRecord && state.schema && state.template ? (
                (() => {
                  // Check if selected record is virtual
                  const virtualRecord = state.template.virtualRecords?.[state.selectedRecord];
                  let recordToPass;

                  if (virtualRecord) {
                    // Handle multi-record virtual records
                    if (virtualRecord.isMultiRecord && virtualRecord.baseRecords) {
                      // For multi-record, show a different UI that displays all records
                      return (
                        <Box>
                          {renderQuantityWarningToggle(state.selectedRecord)}
                          <Alert mb="md" color="violet" variant="light">
                            <Text size="sm">
                              This is a multi-record form containing {virtualRecord.baseRecords.length} records.
                              Field configuration for multi-record forms is managed per-record below.
                            </Text>
                          </Alert>
                          <Stack gap="xl">
                            {virtualRecord.multiRecordConfig?.map((config, index) => {
                              const baseRecord = state.schema!.records.find(r => r.record === config.recordName);
                              if (!baseRecord) return null;

                              return (
                                <Box key={config.recordName}>
                                  <Group mb="md">
                                    <Text fw={600} size="lg">{config.recordName}</Text>
                                    <Badge size="sm" variant="outline">Record {index + 1}</Badge>
                                  </Group>
                                  <GenevaFieldsTable
                                    record={baseRecord}
                                    template={state.template!}
                                    onFieldOverrideChange={(_, fieldXmlTag, override) => {
                                      // Update field override in the multi-record config
                                      updateMultiRecordFieldOverride(state.selectedRecord!, config.recordName, fieldXmlTag, override);
                                    }}
                                    onFieldSelect={(fieldXmlTag: string) =>
                                      setState(prev => ({
                                        ...prev,
                                        selectedField: fieldXmlTag,
                                        drawerOpen: true
                                      }))
                                    }
                                    isMultiRecord={true}
                                    virtualRecordId={state.selectedRecord!}
                                    baseRecordName={config.recordName}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      );
                    } else {
                      // Single virtual record (backward compatibility)
                      // For recursive inheritance, find the ultimate base record
                      let ultimateBaseRecord = null;
                      let currentRecordId = virtualRecord.base;

                      // Walk the chain to find the ultimate base record
                      while (currentRecordId) {
                        const realRecord = state.schema.records.find(r => r.record === currentRecordId);
                        if (realRecord) {
                          ultimateBaseRecord = realRecord;
                          break;
                        }

                        // Check if it's a virtual record
                        const virtualBase = state.template.virtualRecords?.[currentRecordId];
                        if (virtualBase?.base) {
                          currentRecordId = virtualBase.base;
                        } else {
                          break;
                        }
                      }

                      if (!ultimateBaseRecord) {
                        return (
                          <Box ta="center" py="xl">
                            <Text c="red">Virtual record cannot find ultimate base record from: {virtualRecord.base}</Text>
                          </Box>
                        );
                      }
                      recordToPass = {
                        record: state.selectedRecord, // Use virtual record ID
                        fields: ultimateBaseRecord.fields,    // Use ultimate base record fields
                      };
                    }
                  } else {
                    // For real records, find directly in schema
                    recordToPass = state.schema.records.find(r => r.record === state.selectedRecord);
                    if (!recordToPass) {
                      return (
                        <Box ta="center" py="xl">
                          <Text c="red">Record not found: {state.selectedRecord}</Text>
                        </Box>
                      );
                    }
                  }

                  // Only show regular fields table if we have a record to pass
                  return recordToPass ? (
                    <Stack gap="md">
                      {renderQuantityWarningToggle(state.selectedRecord)}
                      <GenevaFieldsTable
                        record={recordToPass}
                        template={state.template}
                        onFieldOverrideChange={updateFieldOverride}
                        onFieldSelect={(fieldXmlTag: string) =>
                          setState(prev => ({
                            ...prev,
                            selectedField: fieldXmlTag,
                            drawerOpen: true
                          }))
                        }
                      />
                    </Stack>
                  ) : null;
                })()
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">Select a record from the left panel to view its fields</Text>
                  <Text size="sm" c="dimmed" mt="xs">
                    Click a record checkbox on the left to enable it, then click the record name to view its fields
                  </Text>
                </Box>
              )}
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="templates" pt="md">
          <ValidationTemplatesManager
            templates={state.template?.validationTemplates}
            onTemplatesChange={updateValidationTemplates}
            records={state.schema?.records}
          />
        </Tabs.Panel>
      </Tabs>

      {/* Virtual Record Creation Modal */}
      <Modal
        opened={createVirtualModal}
        onClose={() => {
          setCreateVirtualModal(false);
          setNewVirtualName('');
          setNewVirtualBase(null);
          setIsMultiRecord(false);
          setSelectedRecords([]);
        }}
        title="Create Virtual Record"
        size="md"
      >
        <Stack gap="md">
          <Switch
            label="Multi-Record Form"
            description="Create a form that combines multiple records into a single XML file"
            checked={isMultiRecord}
            onChange={(e) => {
              setIsMultiRecord(e.currentTarget.checked);
              // Reset selections when switching modes
              setNewVirtualBase(null);
              setSelectedRecords([]);
            }}
          />

          <Text size="sm" c="dimmed">
            {isMultiRecord
              ? "Create a form that combines multiple records. This is useful when you need to submit related records together (e.g., Portfolio + Position + Transaction)."
              : "Create a specialized variant of an existing record with its own field overrides and defaults."}
          </Text>

          {isMultiRecord ? (
            <MultiSelect
              label="Select Records"
              placeholder="Choose records to combine"
              data={state.schema?.records.map(r => ({ value: r.record, label: r.record })) || []}
              value={selectedRecords}
              onChange={setSelectedRecords}
              searchable
              required
              description="Select 2 or more records to include in this multi-record form"
            />
          ) : (
            <Select
              label="Base Record"
              placeholder="Select a record to extend"
              data={(() => {
                try {
                  const realRecords = state.schema?.records?.map(r => r?.record).filter(Boolean) || [];
                  const virtualRecords = state.template?.virtualRecords
                    ? Object.keys(state.template.virtualRecords)
                    : [];

                  const allRecords = [...realRecords, ...virtualRecords];
                  console.log('Available records for inheritance:', allRecords);
                  return allRecords;
                } catch (error) {
                  console.error('Error generating select data:', error);
                  return [];
                }
              })()}
              value={newVirtualBase || ''}
              onChange={setNewVirtualBase}
              searchable
              required
            />
          )}

          <TextInput
            label={isMultiRecord ? "Form Name" : "Variant Name"}
            placeholder={isMultiRecord ? "e.g., Portfolio & Position Bundle" : "e.g., DPM, NonDPM, Special"}
            value={newVirtualName}
            onChange={(e) => setNewVirtualName(e.target.value)}
            required
            description={isMultiRecord
              ? "Give this multi-record form a descriptive name"
              : "This will create a record named like 'Portfolio – DPM'"}
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setCreateVirtualModal(false);
                setNewVirtualName('');
                setNewVirtualBase(null);
                setIsMultiRecord(false);
                setSelectedRecords([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateVirtualRecord}
              disabled={
                !newVirtualName.trim() ||
                (isMultiRecord ? selectedRecords.length < 2 : !newVirtualBase)
              }
            >
              Create Virtual Record
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Field Editor Drawer */}
      <GenevaFieldDrawer
        opened={state.drawerOpen}
        onClose={() => setState(prev => ({ ...prev, drawerOpen: false, selectedField: null }))}
        record={state.selectedRecord}
        field={state.selectedField}
        schema={state.schema}
        template={state.template}
        onFieldOverrideChange={updateFieldOverride}
      />
    </Container>
  );
}; 
